import { useEffect, useRef } from 'react';

/**
 * Zonas donde un clic no cierra el panel de detalle: los elementos marcados
 * con `data-keep-detail` (filas, filtros, el propio panel…) y todo lo que va
 * en un portal fuera de #root (menús ⋮, modales).
 */
export const isDetailKeepZone = (target: Element) => {
  const root = document.getElementById('root');
  if (root && !root.contains(target)) return true;
  if (target.closest('[data-keep-detail]')) return true;
  // Plegar el menú lateral no cierra el panel de detalle abierto, sea drawer o
  // no (maestros de Finanzas, Canales de venta): .sidebar-form-section está en
  // todos, también dentro de los drawers.
  return Boolean(target.closest('.sidebar-toggle-btn') && document.querySelector('.sidebar-form-section'));
};

/**
 * Llama a `onOutside` cuando se hace clic fuera de lo que `isInside` reconozca.
 *
 * Escucha en fase de captura para decidir antes de que React procese el clic:
 * en React 16 sus manejadores corren antes que cualquier listener de document
 * añadido en un efecto, y para entonces el nodo pulsado puede haber
 * desaparecido del DOM (p. ej. un botón que se sustituye al pulsarlo).
 *
 * Exige que también el mousedown haya sido fuera: así, seleccionar texto en un
 * input y soltar fuera no cuenta como clic fuera.
 */
export const useClickOutside = (
  isInside: (target: Element) => boolean,
  onOutside: () => void,
  enabled: boolean
) => {
  // Refs para no re-suscribir los listeners en cada render.
  const isInsideRef = useRef(isInside);
  const onOutsideRef = useRef(onOutside);
  isInsideRef.current = isInside;
  onOutsideRef.current = onOutside;

  useEffect(() => {
    if (!enabled) return;

    let pressedOutside = false;
    const isOutside = (e: Event) => e.target instanceof Element && !isInsideRef.current(e.target);

    const handleMouseDown = (e: MouseEvent) => {
      pressedOutside = isOutside(e);
    };

    const handleClick = (e: MouseEvent) => {
      // detail === 0: clic generado con teclado (Enter/Espacio), sin mousedown previo.
      const startedOutside = e.detail === 0 || pressedOutside;
      pressedOutside = false;
      if (startedOutside && isOutside(e)) onOutsideRef.current();
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('click', handleClick, true);
    };
  }, [enabled]);
};
